import { cn } from '@/lib/utils';

export interface Point {
  x: number;
  y: number;
}

/**
 * SVG path for a closed polygon whose every corner is rounded by `r`, using a
 * quadratic curve through each vertex. Radius is clamped to half of each
 * adjacent edge so the acute corners of a trapezoid/rhombus never self-cross.
 * `points` must be ordered (clockwise) and form a simple polygon.
 * Used for the rhombus New-Tab button (all corners rounded inward).
 */
export function roundedPolygonPath(points: Point[], r: number): string {
  const n = points.length;
  if (n < 3) return '';
  const seg: string[] = [];

  for (let i = 0; i < n; i++) {
    const prev = points[(i - 1 + n) % n];
    const curr = points[i];
    const next = points[(i + 1) % n];

    const v1 = { x: curr.x - prev.x, y: curr.y - prev.y };
    const v2 = { x: next.x - curr.x, y: next.y - curr.y };
    const len1 = Math.hypot(v1.x, v1.y) || 1;
    const len2 = Math.hypot(v2.x, v2.y) || 1;
    const rad = Math.min(r, len1 / 2, len2 / 2);

    const p1 = {
      x: curr.x - (v1.x / len1) * rad,
      y: curr.y - (v1.y / len1) * rad,
    };
    const p2 = {
      x: curr.x + (v2.x / len2) * rad,
      y: curr.y + (v2.y / len2) * rad,
    };

    seg.push(i === 0 ? `M ${p1.x} ${p1.y}` : `L ${p1.x} ${p1.y}`);
    seg.push(`Q ${curr.x} ${curr.y} ${p2.x} ${p2.y}`);
  }
  seg.push('Z');
  return seg.join(' ');
}

/**
 * Trapezoid tab path (wide bottom, narrow top):
 *  - TOP corners round INWARD (normal rounded corner), radius `rTop`.
 *  - BOTTOM corners flare OUTWARD as a "ramp": a curve tangent to the slanted
 *    side and tangent to the horizontal toolbar line (y = h), bulging past the
 *    corner so the outline is one continuous line from the toolbar surface up
 *    into the tab's side — no inward notch / gap at the merge seam.
 * Returns the path plus `padX`, the horizontal overflow (px) the ramps add on
 * each side, so the caller can size the SVG viewBox to avoid clipping the flare.
 */
export function trapezoidTabPath(
  w: number,
  h: number,
  ear: number,
  rTop: number,
  rBottom: number,
  rampDy = 0,
  skirt = 0,
): { d: string; dOpen: string; dSkirt: string; padX: number } {
  // The bottom landing line (and with it the whole ramp/fillet region) can be
  // slid vertically by `rampDy`. The slanted sides run from the fixed top
  // corners (y=0) down to the shifted bottom corners (y=hb), so the ramps stay
  // tangent — no kink — they just move up/down as a unit.
  const hb = h + rampDy;
  const L = Math.hypot(ear, hb) || 1; // slanted-side length
  const ux = ear / L; // unit components along the side
  const uy = hb / L;

  // clamp radii so they never exceed the geometry
  const rt = Math.min(rTop, (w - 2 * ear) / 2 || 0, L / 2);
  const rb = Math.max(0, Math.min(rBottom, L / 2));

  // --- top corners (inward) ---
  const TL = { x: ear, y: 0 };
  const TR = { x: w - ear, y: 0 };
  const tlSide = { x: TL.x - ux * rt, y: TL.y + uy * rt }; // backoff down the L side
  const tlTop = { x: TL.x + rt, y: 0 };
  const trTop = { x: TR.x - rt, y: 0 };
  const trSide = { x: TR.x + ux * rt, y: TR.y + uy * rt }; // backoff down the R side

  // --- bottom ramps (outward, tangent to side + tangent to y=hb) ---
  // Left ramp: tangent circle of radius rb sits in the lower-left sector.
  const cxL = (rb * (ear - L)) / hb; // x of horizontal tangent point (<= 0)
  const thL = { x: cxL, y: hb }; // lands on toolbar line, flared left
  const tsL = { x: cxL + (rb * hb) / L, y: hb - rb + (rb * ear) / L }; // on the side
  const thR = { x: w - cxL, y: hb }; // mirror, flared right
  const tsR = { x: w - (cxL + (rb * hb) / L), y: tsL.y };

  const c = rb * 0.55; // cubic handle length (circle approximation)

  // left ramp control handles: horizontal at thL, along-side at tsL
  const thL_c = { x: thL.x + c, y: thL.y };
  const tsL_c = { x: tsL.x - ux * c, y: tsL.y + uy * c };
  // right ramp control handles
  const tsR_c = { x: tsR.x + ux * c, y: tsR.y + uy * c };
  const thR_c = { x: thR.x - c, y: thR.y };

  // Open outline: thL -> ramp up -> left side -> top -> right side -> ramp down
  // -> thR. No bottom edge, so when stroked it merges into the toolbar seam.
  const open = [
    `M ${thL.x} ${thL.y}`,
    `C ${thL_c.x} ${thL_c.y} ${tsL_c.x} ${tsL_c.y} ${tsL.x} ${tsL.y}`, // ramp up
    `L ${tlSide.x} ${tlSide.y}`, // straight left side
    `Q ${TL.x} ${TL.y} ${tlTop.x} ${tlTop.y}`, // top-left inward
    `L ${trTop.x} ${trTop.y}`, // top edge
    `Q ${TR.x} ${TR.y} ${trSide.x} ${trSide.y}`, // top-right inward
    `L ${tsR.x} ${tsR.y}`, // straight right side
    `C ${tsR_c.x} ${tsR_c.y} ${thR_c.x} ${thR_c.y} ${thR.x} ${thR.y}`, // ramp down
  ].join(' ');

  const dOpen = open;
  const d = `${open} Z`; // closed (bottom edge) for fill / non-merged stroke

  // Vertical skirt: extend the fill straight DOWN from the flared bottom corners
  // (NO widening — sides stay at thL.x/thR.x) so its bottom edge, and the
  // inter-layer hairline it forms against the separate fixed toolbar at
  // fractional scaling, lands well INSIDE the toolbar, away from the visible
  // merge. Used for the active/merged tab's fill only.
  const dSkirt =
    skirt > 0
      ? `${open} L ${thR.x} ${hb + skirt} L ${thL.x} ${hb + skirt} Z`
      : d;

  const padX = Math.ceil(Math.max(0, -cxL)) + 1;
  return { d, dOpen, dSkirt, padX };
}

export type ShapeVariant = 'trapezoid' | 'rhombus-left';

interface TabShapeProps {
  width: number;
  height: number;
  /** trapezoid: slant inset per side. rhombus: top-edge left-shift. */
  ear: number;
  /** top corner radius (rhombus: all corners) */
  radius: number;
  /** trapezoid bottom ramp radius; defaults to `radius` */
  bottomRadius?: number;
  fill: string;
  /** 0..1 fill alpha (translucency) */
  fillOpacity?: number;
  /** opaque backing painted BEHIND the fill (same shape). Makes a translucent
   *  fill read against THIS color (the background) instead of whatever tabs are
   *  stacked beneath — i.e. translucent to the bg, but opaque to tabs behind. */
  occluder?: string;
  /** stroke color; omit for no stroke */
  stroke?: string;
  strokeWidth?: number;
  /**
   * trapezoid only: stroke the outline WITHOUT the bottom edge, so the tab's
   * outline flows into the toolbar seam instead of drawing a line across the
   * merge. (Fill still uses the closed shape.)
   */
  openBottom?: boolean;
  /** trapezoid only: slide the bottom ramp/fillet landing line vertically (px) */
  rampDy?: number;
  variant: ShapeVariant;
  className?: string;
  /** forwarded to the <path> so the SHAPE (not its bbox) is the hit area */
  onMouseDown?: (e: React.MouseEvent) => void;
}

/**
 * Absolutely fills its (position:relative) parent and paints a rounded
 * trapezoid / rhombus. Container is pointer-events:none; only the filled path
 * is interactive, so transparent corners fall through to whatever is stacked
 * beneath (required for the right-over-left fan).
 */
export function TabShape({
  width,
  height,
  ear,
  radius,
  bottomRadius,
  fill,
  fillOpacity = 1,
  occluder,
  stroke,
  strokeWidth = 1,
  openBottom = false,
  rampDy = 0,
  variant,
  className,
  onMouseDown,
}: TabShapeProps) {
  const w = width;
  const h = height;
  const sw = stroke ? strokeWidth : 0;
  const sp = stroke ? Math.ceil(sw) + 1 : 0; // vertical headroom so stroke isn't clipped

  let d: string; // closed fill path
  let dStroke: string; // path used for the outline
  let padX = 0;
  // extra viewBox headroom for a shifted bottom ramp (down = below h, up = above 0)
  let overTop = 0;
  let overBot = 0;

  if (variant === 'trapezoid') {
    const t = trapezoidTabPath(
      w,
      h,
      ear,
      radius,
      bottomRadius ?? radius,
      rampDy,
    );
    // For a merged (open-bottom) tab the visible border is the rampDy-shifted
    // OPEN outline, but the FILL is a rampDy=0 shape with a vertical SKIRT — it
    // runs straight down past the seam, deep into the toolbar, so the fill's
    // bottom edge (an inter-layer hairline vs the separate fixed toolbar at
    // fractional scaling — the actual "ghost line") sits well below the visible
    // merge instead of right at it. SKIRT_PX past the baseline.
    const SKIRT_PX = sp + 2;
    const tFill = openBottom
      ? trapezoidTabPath(w, h, ear, radius, bottomRadius ?? radius, 0, SKIRT_PX)
      : t;
    d = openBottom ? tFill.dSkirt : tFill.d;
    dStroke = openBottom ? t.dOpen : t.d;
    padX = Math.max(t.padX, tFill.padX);
    overTop = Math.max(0, -rampDy);
    overBot = openBottom ? Math.max(0, rampDy, SKIRT_PX) : Math.max(0, rampDy);
  } else {
    // rhombus-left: top edge shifted LEFT of the bottom edge, all corners inward
    const pts: Point[] = [
      { x: 0, y: 0 },
      { x: w - ear, y: 0 },
      { x: w, y: h },
      { x: ear, y: h },
    ];
    d = roundedPolygonPath(pts, radius);
    dStroke = d;
  }

  const vbX = -padX;
  const vbY = -(sp + overTop);
  const vbW = w + 2 * padX;
  const vbH = h + 2 * sp + overTop + overBot;

  return (
    <svg
      className={cn('pointer-events-none absolute', className)}
      style={{ left: vbX, top: vbY }}
      width={vbW}
      height={vbH}
      viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
      preserveAspectRatio="none"
    >
      {/* occluder — opaque backing (the bg color) so a translucent fill never
          reveals the tabs stacked behind it, only the background */}
      {occluder && (
        <path
          d={d}
          fill={occluder}
          stroke="none"
          className="pointer-events-none"
        />
      )}
      {/* fill — also the hit area (shape-accurate) */}
      <path
        d={d}
        fill={fill}
        fillOpacity={fillOpacity}
        stroke="none"
        className="pointer-events-auto"
        onMouseDown={onMouseDown}
      />
      {/* outline — open at the bottom when merging into the seam */}
      {stroke && (
        <path
          d={dStroke}
          fill="none"
          stroke={stroke}
          strokeWidth={sw}
          strokeLinejoin="round"
          className="pointer-events-none"
        />
      )}
    </svg>
  );
}
